import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AppContext } from '../../../context/Appcontext';
import { FileText, Users, Award, UserCheck, Download, Calendar, Filter, ChevronLeft, Eye, FileDown, Printer, Mail } from 'lucide-react';
import '../../../assests/css/admin/AdminReports/AdminReports.css';
import Adminmasterlayout from '../../../layouts/Adminmasterlayout/Adminmasterlayout';

const AdminReports = () => {
  const { baseUrl, baseUrlForFiles } = useContext(AppContext);
  const [selectedReport, setSelectedReport] = useState(null);
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [viewMode, setViewMode] = useState('main'); // 'main', 'view', 'download'
  const [activeReportData, setActiveReportData] = useState(null);
  const [feeReportData, setFeeReportData] = useState([]);
  const [feeReportLoading, setFeeReportLoading] = useState(false);
  const [attendanceReportData, setAttendanceReportData] = useState([]);
  const [attendanceReportLoading, setAttendanceReportLoading] = useState(false);
  const [examResultReportData, setExamResultReportData] = useState([]);
  const [examResultReportLoading, setExamResultReportLoading] = useState(false);
  const [teacherAttendanceReportData, setTeacherAttendanceReportData] = useState([]);
  const [teacherAttendanceReportLoading, setTeacherAttendanceReportLoading] = useState(false);

  const fetchFeeReport = async () => {
    try {
      setFeeReportLoading(true);

      const response = await axios.get(
        `${baseUrl}/admin/fee/adminFeeReport`
      );

      console.log("Fee Report Response:", response.data);

      if (response.data.success) {
        setFeeReportData(response.data.feeReports);
      }

    } catch (error) {
      console.log("Fee Report Error:", error);
    } finally {
      setFeeReportLoading(false);
    }
  };

  const fetchAttendanceReport = async () => {
    try {
      setAttendanceReportLoading(true);

      const response = await axios.get(
        `${baseUrl}/admin/attendance/adminAttendanceReport`
      );

      console.log("Attendance Report Response:", response.data);

      if (response.data.success) {
        setAttendanceReportData(response.data.attendanceReports);
      }

    } catch (error) {
      console.log("Attendance Report Error:", error);
    } finally {
      setAttendanceReportLoading(false);
    }
  };

  const fetchExamResultReport = async () => {
    try {
      setExamResultReportLoading(true);

      const response = await axios.get(
        `${baseUrl}/admin/result/adminExamResultReport`
      );

      console.log("Exam Result Report Response:", response.data);

      if (response.data.success) {
        setExamResultReportData(response.data.examResults);
      }

    } catch (error) {
      console.log("Exam Result Report Error:", error);
    } finally {
      setExamResultReportLoading(false);
    }
  };

  const fetchTeacherAttendanceReport = async () => {
    try {
      setTeacherAttendanceReportLoading(true);

      const response = await axios.get(
        `${baseUrl}/admin/attendance/adminTeacherAttendanceReport`
      );

      console.log("Teacher Attendance Report Response:", response.data);

      if (response.data.success) {
        setTeacherAttendanceReportData(
          response.data.teacherAttendanceReports
        );
      } else {
        setTeacherAttendanceReportData([]);
      }

    } catch (error) {
      console.log(
        "Teacher Attendance Report Error:",
        error.response?.data || error
      );

      setTeacherAttendanceReportData([]);
    } finally {
      setTeacherAttendanceReportLoading(false);
    }
  };

  useEffect(() => {
    fetchFeeReport();
  }, []);

  const reportTypes = [
    {
      id: 'fee',
      title: 'Fee Reports',
      description: 'View and download student fee payment records',
      icon: FileText,
      color: '#6A88CE',
      stats: { total: 1234, pending: 45 }
    },
    {
      id: 'attendance',
      title: 'Attendance Reports',
      description: 'Track student attendance and generate summaries',
      icon: Users,
      color: '#E4916D',
      stats: { total: 856, present: 782 }
    },
    {
      id: 'exam',
      title: 'Exam Results Reports',
      description: 'Access comprehensive exam results and analytics',
      icon: Award,
      color: '#D1CFE3',
      stats: { total: 342, passed: 298 }
    },

    {
      id: 'teacherAttendance',
      title: 'Teacher Attendance Reports',
      description: 'Track teacher attendance and generate attendance summaries',
      icon: UserCheck,
      color: '#6A88CE',
      stats: {
        total: 48,
        present: 43
      }
    }
  ];

  // Sample data for different report types
  const sampleReportData = {
    fee: [
      { id: 1, name: 'Ahmad Hassan', grade: '10th', amount: 15000, paid: 15000, pending: 0, status: 'Paid', date: '2024-01-15' },
      { id: 2, name: 'Fatima Khan', grade: '9th', amount: 15000, paid: 10000, pending: 5000, status: 'Partial', date: '2024-01-20' },
      { id: 3, name: 'Ali Raza', grade: '11th', amount: 18000, paid: 18000, pending: 0, status: 'Paid', date: '2024-01-10' },
      { id: 4, name: 'Ayesha Malik', grade: '10th', amount: 15000, paid: 0, pending: 15000, status: 'Unpaid', date: '2024-01-05' },
      { id: 5, name: 'Hassan Ali', grade: '8th', amount: 12000, paid: 12000, pending: 0, status: 'Paid', date: '2024-01-18' }
    ],
    attendance: [
      { id: 1, name: 'Ahmad Hassan', grade: '10th', present: 22, absent: 2, leaves: 1, percentage: '88%', status: 'Good' },
      { id: 2, name: 'Fatima Khan', grade: '9th', present: 20, absent: 4, leaves: 1, percentage: '80%', status: 'Average' },
      { id: 3, name: 'Ali Raza', grade: '11th', present: 24, absent: 1, leaves: 0, percentage: '96%', status: 'Excellent' },
      { id: 4, name: 'Ayesha Malik', grade: '10th', present: 18, absent: 6, leaves: 1, percentage: '72%', status: 'Poor' },
      { id: 5, name: 'Hassan Ali', grade: '8th', present: 23, absent: 2, leaves: 0, percentage: '92%', status: 'Good' }
    ],
    exam: [
      { id: 1, name: 'Ahmad Hassan', grade: '10th', subject: 'Mathematics', marks: 85, total: 100, percentage: '85%', status: 'Pass' },
      { id: 2, name: 'Fatima Khan', grade: '9th', subject: 'English', marks: 78, total: 100, percentage: '78%', status: 'Pass' },
      { id: 3, name: 'Ali Raza', grade: '11th', subject: 'Physics', marks: 92, total: 100, percentage: '92%', status: 'Pass' },
      { id: 4, name: 'Ayesha Malik', grade: '10th', subject: 'Chemistry', marks: 45, total: 100, percentage: '45%', status: 'Fail' },
      { id: 5, name: 'Hassan Ali', grade: '8th', subject: 'Biology', marks: 88, total: 100, percentage: '88%', status: 'Pass' }
    ]
  };

  const handleGenerateReport = (reportId) => {
    setSelectedReport(reportId);
    setActiveReportData(reportTypes.find(r => r.id === reportId));
  };

  const handleViewDetails = (reportId) => {
    setActiveReportData(reportTypes.find(r => r.id === reportId));
    setSelectedReport(reportId);
    setViewMode('view');

    if (reportId === 'fee') {
      fetchFeeReport();
    }

    if (reportId === 'attendance') {
      fetchAttendanceReport();
    }

    if (reportId === 'exam') {
      fetchExamResultReport();
    }
    if (reportId === 'teacherAttendance') {
      fetchTeacherAttendanceReport();
    }
  };

  const handleDownload = (reportId) => {
    setActiveReportData(reportTypes.find(r => r.id === reportId));
    setSelectedReport(reportId);
    setViewMode('download');
  };

  const handleBackToMain = () => {
    setViewMode('main');
    setSelectedReport(null);
  };

  const handlePrint = () => {
    window.print();
  };

  const handleExport = (format) => {
    console.log(`Exporting report as ${format}`);
    // Export logic here
  };

  // Main Reports Grid View
  if (viewMode === 'main') {
    return (
      <Adminmasterlayout>
        <div className="admin-reports-wrapper">
          <div className="admin-reports-container">
            <div className="admin-reports-header">
              <div className="admin-reports-header-content">
                <h1 className="admin-reports-title">Reports Management</h1>
                <p className="admin-reports-subtitle">Generate and download comprehensive reports</p>
              </div>
              <div className="admin-reports-header-actions">
                <button className="admin-reports-filter-btn">
                  <Filter size={18} />
                  <span>Filter</span>
                </button>
              </div>
            </div>

            <div className="admin-reports-date-filter">
              <div className="admin-reports-date-input-group">
                <Calendar size={18} />
                <input
                  type="date"
                  className="admin-reports-date-input"
                  value={dateRange.start}
                  onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                  placeholder="Start Date"
                />
              </div>
              <span className="admin-reports-date-separator">to</span>
              <div className="admin-reports-date-input-group">
                <Calendar size={18} />
                <input
                  type="date"
                  className="admin-reports-date-input"
                  value={dateRange.end}
                  onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                  placeholder="End Date"
                />
              </div>
            </div>

            <div className="admin-reports-grid">
              {reportTypes.map((report) => {
                const IconComponent = report.icon;
                return (
                  <div
                    key={report.id}
                    className={`admin-reports-card ${selectedReport === report.id ? 'admin-reports-card-active' : ''}`}
                    onClick={() => handleGenerateReport(report.id)}
                  >
                    <div className="admin-reports-card-header">
                      <div className="admin-reports-card-icon" style={{ backgroundColor: report.color }}>
                        <IconComponent size={24} color="#FEFEFF" />
                      </div>
                      <h3 className="admin-reports-card-title">{report.title}</h3>
                    </div>

                    <p className="admin-reports-card-description">{report.description}</p>

                    <div className="admin-reports-card-stats">
                      <div className="admin-reports-stat-item">
                        <span className="admin-reports-stat-label">Total</span>
                        <span className="admin-reports-stat-value">{report.stats.total}</span>
                      </div>
                      <div className="admin-reports-stat-item">
                        <span className="admin-reports-stat-label">
                          {report.id === 'fee' ? report.stats.pending : report.id === 'attendance' ? report.stats.present : report.stats.passed}
                        </span>
                        <span className="admin-reports-stat-value" style={{ color: report.color }}>
                          {report.id === 'fee' ? report.stats.pending : report.id === 'attendance' ? report.stats.present : report.stats.passed}
                        </span>
                      </div>
                    </div>

                    <div className="admin-reports-card-actions">
                      <button
                        className="admin-reports-action-btn admin-reports-view-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewDetails(report.id);
                        }}
                      >
                        <Eye size={16} />
                        View Details
                      </button>
                      {/* <button 
                      className="admin-reports-action-btn admin-reports-download-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownload(report.id);
                      }}
                    >
                      <Download size={16} />
                      Download
                    </button> */}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </Adminmasterlayout>
    );
  }

  // View Details Page
  if (viewMode === 'view' && activeReportData) {
    const reportData = selectedReport === 'fee' ? feeReportData : selectedReport === 'attendance' ? attendanceReportData : selectedReport === 'exam' ? examResultReportData : selectedReport === 'teacherAttendance' ? teacherAttendanceReportData : sampleReportData[selectedReport] || [];

    return (
      <div className="admin-reports-wrapper">
        <div className="admin-reports-view-container">
          <div className="admin-reports-view-header">
            <button className="admin-reports-back-btn" onClick={handleBackToMain}>
              <ChevronLeft size={20} />
              Back to Reports
            </button>
            <div className="admin-reports-view-title-section">
              <h1 className="admin-reports-view-title">{activeReportData.title} - Detailed View</h1>
              <p className="admin-reports-view-subtitle">Comprehensive data analysis and insights</p>
            </div>
            <div className="admin-reports-view-actions">
              {/* <button className="admin-reports-view-action-btn" onClick={handlePrint}>
                <Printer size={18} />
                Print
              </button> */}
              {/* <button className="admin-reports-view-action-btn" onClick={() => handleExport('pdf')}>
                <FileDown size={18} />
                Export PDF
              </button> */}
            </div>
          </div>

          <div className="admin-reports-view-content">
            <div className="admin-reports-table-wrapper">
              <table className="admin-reports-table">
                <thead className="admin-reports-table-head">
                  <tr>
                    {selectedReport === 'fee' && (
                      <>
                        <th>ID</th>
                        <th>Student Name</th>
                        <th>Registration No</th>
                        <th>Semester</th>
                        <th>Amount</th>
                        <th>Status</th>
                        <th>Receipt</th>
                        <th>Submitted At</th>
                      </>
                    )}
                    {selectedReport === 'attendance' && (
                      <>
                        <th>ID</th>
                        <th>Student Name</th>
                        <th>Registration No</th>
                        <th>Course</th>
                        <th>Date</th>
                        <th>Status</th>
                      </>
                    )}
                    {selectedReport === 'exam' && (
                      <>
                        <th>ID</th>
                        <th>Student Name</th>
                        <th>Registration No</th>
                        <th>Course</th>
                        <th>Obtained Marks</th>
                        <th>Total Marks</th>
                        <th>Percentage</th>
                        <th>Status</th>
                      </>
                    )}

                    {selectedReport === 'teacherAttendance' && (
                      <>
                        <th>ID</th>
                        <th>Teacher Name</th>
                        <th>Email</th>
                        <th>Course</th>
                        <th>Present</th>
                        <th>Absent</th>
                        <th>Total Classes</th>
                        <th>Attendance %</th>
                        <th>Status</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody className="admin-reports-table-body">
                  {reportData.map((row) => (
                    <tr key={row.id}>
                      {selectedReport === 'fee' && (
                        <>
                          <td>{row.id}</td>

                          <td>
                            {row?.ngstudent?.fullname || 'N/A'}
                          </td>

                          <td>
                            {row?.ngstudent?.regno || 'N/A'}
                          </td>

                          <td>
                            {row?.ngstudent?.semester || row?.ngfeechallan?.semester || 'N/A'}
                          </td>

                          <td>
                            Rs. {row?.ngfeechallan?.amount || 0}
                          </td>

                          <td>
                            <span
                              className={`admin-reports-status-badge admin-reports-status-${row.status?.toLowerCase()}`}
                            >
                              {row.status}
                            </span>
                          </td>

                          <td>
                            {row.image ? (
                              <a
                                href={`${baseUrlForFiles}/${row.image}`}
                                target="_blank"
                                rel="noreferrer"
                              >
                                <button className="admin-reports-action-btn admin-reports-view-btn">
                                  <Eye size={16} />
                                  View Receipt
                                </button>
                              </a>
                            ) : (
                              "No Receipt")}
                          </td>

                          <td>
                            {row.created_at ? new Date(row.created_at).toLocaleDateString() : "N/A"}
                          </td>
                        </>
                      )}
                      {selectedReport === 'attendance' && (
                        <>
                          <td>{row.id}</td>

                          <td>
                            {row?.ngstudentcourse?.ngstudent?.fullname || 'N/A'}
                          </td>

                          <td>
                            {row?.ngstudentcourse?.ngstudent?.regno || 'N/A'}
                          </td>

                          <td>
                            {row?.ngteachercourse?.ngcourse?.coursename || 'N/A'}
                          </td>

                          <td>
                            {row.date || 'N/A'}
                          </td>

                          <td>
                            <span
                              className={`admin-reports-status-badge admin-reports-status-${row.status?.toLowerCase()}`}
                            >
                              {row.status || 'N/A'}
                            </span>
                          </td>
                        </>
                      )}
                      {selectedReport === 'exam' && (
                        <>
                          <td>{row.id}</td>

                          <td>
                            {row?.ngstudent?.fullname || 'N/A'}
                          </td>

                          <td>
                            {row?.ngstudent?.regno || 'N/A'}
                          </td>

                          <td>
                            {row?.ngcourse?.coursename || 'N/A'}
                          </td>

                          <td>
                            {row?.obtainMarks ?? 0}
                          </td>

                          <td>
                            100
                          </td>

                          <td>
                            {row?.obtainMarks !== undefined
                              ? `${row.obtainMarks}%`
                              : 'N/A'}
                          </td>

                          <td>
                            <span
                              className={`admin-reports-status-badge admin-reports-status-${Number(row?.obtainMarks) >= 40 ? 'pass' : 'fail'
                                }`}
                            >
                              {Number(row?.obtainMarks) >= 40 ? 'Pass' : 'Fail'}
                            </span>
                          </td>
                        </>
                      )}

                      {selectedReport === 'teacherAttendance' && (
                        <>
                          <td>{row.id}</td>

                          <td>
                            {row.teacherName || 'N/A'}
                          </td>

                          <td>
                            {row.email || 'N/A'}
                          </td>

                          <td>
                            {row.course || 'N/A'}
                          </td>

                          <td>
                            {row.present ?? 0}
                          </td>

                          <td>
                            {row.absent ?? 0}
                          </td>

                          <td>
                            {row.total ?? 0}
                          </td>

                          <td>
                            {row.percentage || '0%'}
                          </td>

                          <td>
                            <span
                              className={`admin-reports-status-badge admin-reports-status-${row.status?.toLowerCase()}`}
                            >
                              {row.status || 'N/A'}
                            </span>
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // // Download Page
  // if (viewMode === 'download' && activeReportData) {
  //   return (
  //     <div className="admin-reports-wrapper">
  //       <div className="admin-reports-download-container">
  //         <div className="admin-reports-download-header">
  //           <button className="admin-reports-back-btn" onClick={handleBackToMain}>
  //             <ChevronLeft size={20} />
  //             Back to Reports
  //           </button>
  //           <h1 className="admin-reports-download-title">Download {activeReportData.title}</h1>
  //         </div>

  //         <div className="admin-reports-download-content">
  //           <div className="admin-reports-download-card">
  //             <div className="admin-reports-download-icon" style={{ backgroundColor: activeReportData.color }}>
  //               <FileDown size={48} color="#FEFEFF" />
  //             </div>
  //             <h2 className="admin-reports-download-card-title">Select Download Format</h2>
  //             <p className="admin-reports-download-card-subtitle">Choose your preferred format to download the report</p>

  //             <div className="admin-reports-download-options">
  //               <button 
  //                 className="admin-reports-download-option-btn"
  //                 onClick={() => handleExport('pdf')}
  //               >
  //                 <FileText size={32} color="#6A88CE" />
  //                 <span className="admin-reports-download-option-title">PDF Document</span>
  //                 <span className="admin-reports-download-option-desc">Portable Document Format</span>
  //               </button>

  //               <button 
  //                 className="admin-reports-download-option-btn"
  //                 onClick={() => handleExport('excel')}
  //               >
  //                 <FileText size={32} color="#E4916D" />
  //                 <span className="admin-reports-download-option-title">Excel Spreadsheet</span>
  //                 <span className="admin-reports-download-option-desc">Microsoft Excel Format</span>
  //               </button>

  //               <button 
  //                 className="admin-reports-download-option-btn"
  //                 onClick={() => handleExport('csv')}
  //               >
  //                 <FileText size={32} color="#D1CFE3" />
  //                 <span className="admin-reports-download-option-title">CSV File</span>
  //                 <span className="admin-reports-download-option-desc">Comma Separated Values</span>
  //               </button>
  //             </div>

  //             <div className="admin-reports-download-email-section">
  //               <h3 className="admin-reports-download-email-title">
  //                 <Mail size={20} />
  //                 Email Report
  //               </h3>
  //               <div className="admin-reports-download-email-form">
  //                 <input 
  //                   type="email" 
  //                   className="admin-reports-download-email-input" 
  //                   placeholder="Enter email address"
  //                 />
  //                 <button className="admin-reports-download-email-btn">
  //                   Send Report
  //                 </button>
  //               </div>
  //             </div>
  //           </div>
  //         </div>
  //       </div>
  //     </div>
  //   );
  // }

  return null;
};

export default AdminReports;